// src/pages/dashboard/branch/menu-items/components/tabs/LabelsTab.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTag, FiCheck, FiSearch, FiMinus } from 'react-icons/fi';
import { FOOD_TAGS, foodTags } from '../../../../../../../utils/foodTags';
import InputField from '../../../../../../../components/ui/InputField';
import Button from '../../../../../../../components/ui/Button';
import { Category, supabase } from '../../../../../../../utils/supabaseClient';
import { FormData } from '../AddMenuItemModal';
import { BsStars } from "react-icons/bs";

interface LabelsTabProps {
	itemFormData?: FormData & { id: string, ingredients: string[], category: Category | undefined };
	selectedFoodTags: number[];
	onFoodTagsChange: (tags: number[]) => void;
}

const LabelsTab: React.FC<LabelsTabProps> = ({
	itemFormData,
	selectedFoodTags,
	onFoodTagsChange
}) => {
	const { t, i18n } = useTranslation();
	const [searchQuery, setSearchQuery] = useState<string>('')
	const [isLoading, setIsLoading] = useState(false)
	const isRTL = i18n.language === 'ar';

	const filteredTags = FOOD_TAGS.filter(tag => {
		return (
			!searchQuery ||
			tag.labelEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
			tag.labelAr.toLowerCase().includes(searchQuery.toLowerCase())
		)
	});

	const toggleFoodTag = (tagId: number) => {
		if (selectedFoodTags.includes(tagId)) {
			onFoodTagsChange(selectedFoodTags.filter(id => id !== tagId));
		} else {
			onFoodTagsChange([...selectedFoodTags, tagId]);
		}
	};

	const getTagLabel = (tag: typeof FOOD_TAGS[0]) => {
		return isRTL ? tag.labelAr : tag.labelEn;
	};

	const removeTag = (tagId: number) => {
		onFoodTagsChange(selectedFoodTags.filter(value => value !== tagId))
	}

	const getTags = async () => {
		type LabelizerResponse = {
			success?: boolean;
			data?: Array<{ labels?: Record<string, number> }>;
		};

		const body = JSON.stringify({
			labels: foodTags,
			items: [{
				id: itemFormData?.id ?? '',
				name: itemFormData?.name ?? '',
				description: itemFormData?.description ?? '',
				spice_level: itemFormData?.spiceLevel ?? '',
				calories: itemFormData?.calories ?? 0,
				ingrediants: itemFormData?.ingredients ?? [],
				category: itemFormData?.category?.name ?? ''
			}]
		});

		setIsLoading(true);

		try {
			const { data, error } = await supabase.functions.invoke<LabelizerResponse>('labelizer', {
				method: 'POST',
				body
			});

			if (error) {
				throw error;
			}

			if (!data?.success || !Array.isArray(data.data) || data.data.length === 0) {
				return;
			}

			const labels = (data.data[0]?.labels ?? {}) as Record<string, number>;
			const result: number[] = [];

			foodTags.forEach((item, index) => {
				if (labels[item] === 1) {
					result.push(index);
				}
			});

			onFoodTagsChange(result);
		} catch (invokeError) {
			console.error('Unable to auto-label menu item:', invokeError);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="space-y-6">
			<div className='flex items-center justify-between'>
				<div className="flex items-center gap-2">
					<FiTag className="w-5 h-5 text-primary" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						{t('menuItems.foodLabels')}
					</h4>
				</div>
				<Button
					type='button'
					size='sm'
					variant='outline'
					onClick={getTags}
					isLoading={isLoading}
					leftIcon={<BsStars />}
				>
					{t('menuItems.autoLabeling')}
				</Button>
			</div>


			<p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
				{t('menuItems.foodLabelsDescription')}
			</p>

			<InputField
				// label={t('menuItems.name')}
				icon={<FiSearch className="h-5 w-5 text-gray-400" />}
				value={searchQuery}
				type='search'
				onChange={(e) => setSearchQuery(e.target.value)}
				placeholder={t('common.search')}
			/>

			{/* Selected Tags Summary */}
			{selectedFoodTags.length > 0 && (
				<div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
					<div className='flex justify-between'>
						<h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							{t('menuItems.selectedLabels')} ({selectedFoodTags.length})
						</h5>
						<button
							type="button"
							onClick={() => onFoodTagsChange([])}
							disabled={selectedFoodTags.length === 0}
							className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
						>
							{t('menuItems.clearAllLabels')}
						</button>
					</div>
					<div className="flex flex-wrap gap-2">
						{selectedFoodTags.map(tagId => {
							const tag = FOOD_TAGS.find(t => t.id === tagId);
							if (!tag) return null;
							return (
								<span
									key={tagId}
									className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${tag.color}`}
								>
									<FiCheck size={12} />
									{getTagLabel(tag)}
									<button
										type='button'
										className='text-gray-500 hover:text-red-500 cursor-pointer p-0.5 bg-gray-500/10 hover:bg-red-500/20 rounded-full'
										onClick={() => removeTag(tagId)}
									>
										<FiMinus size={12} />
									</button>
								</span>
							);
						})}
					</div>
				</div>
			)}

			{/* Available Tags Grid */}
			<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
				{filteredTags.map(tag => {
					const isSelected = selectedFoodTags.includes(tag.id);
					return (
						<button
							key={tag.id}
							type="button"
							onClick={() => toggleFoodTag(tag.id)}
							className={`p-3 rounded-lg border-2 ${tag.color} transition-all duration-200 text-start cursor-pointer group ${isSelected
								? 'border-primary bg-primary/10 dark:bg-primary/20'
								: 'border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-700/50'
								}`}
						>
							<div className="flex items-center justify-between">
								{tag.icon}
								<span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium`}>
									{getTagLabel(tag)}
								</span>
								<div className={`w-5 h-5 ${isSelected ? 'bg-primary text-white' : 'bg-gray-400/10 text-gray-400/20 group-hover:bg-gray-400/30 group-hover:text-gray-300'}  rounded-full flex items-center justify-center`}>
									<FiCheck size={12} />
								</div>
							</div>
						</button>
					);
				})}
			</div>

			{/* Quick Actions */}
			{/* <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
				<button
					type="button"
					onClick={() => onFoodTagsChange([])}
					disabled={selectedFoodTags.length === 0}
					className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
				>
					{t('menuItems.clearAllLabels')}
				</button>
			</div> */}
		</div>
	);
};

export default LabelsTab;